import React, { useState } from 'react'
import styled from 'styled-components'
import {
  CloseButton,
  Button,
  Flex,
  Link,
  Text,
  Box,
  getPaletteColor,
  Image
} from 'pcln-design-system'
import { Referral } from '@/types'
import useSeti from '@/hooks/useSeti'
import { getLink } from '../utils'

const COOKIE_MAX_AGE = 2592000 // 30 Days

const Container = styled(Flex)`
  background-color: ${getPaletteColor('background.base')};
  color: ${getPaletteColor('text.lightest')};
  padding: 8px;
  @media (min-width: 400px) {
    padding-right: 24px;
  }
`

const StyledLink = styled(Link)`
  flex: 1 1 auto;
  display: flex;
  &:active,
  &:focus,
  &:visited,
  &:hover {
    color: ${getPaletteColor('text.lightest')};
    text-decoration: none;
  }
`

const appIconUrl =
  'https://s1.pclncdn.com/design-assets/brand-assets/app-icon.svg'

type AppBannerProps = {
  isAndroid: boolean
  titleText: string
  contentText: string
  pageId: string
  referral: Referral | Record<string, never>
  clientGUID: string
}

export default function AppBannerExperiment({
  isAndroid,
  titleText,
  contentText,
  pageId,
  referral,
  clientGUID
}: AppBannerProps) {
  const [isBannerVisible, setBannerVisible] = useState(true)

  const closeAppBanner = () => {
    setBannerVisible(false)
    document.cookie = `hideAppBanner=true; domain=.priceline.com; path=/; max-age=${COOKIE_MAX_AGE};`
  }

  const appBannerNoDismiss =
    useSeti('MBL_HP_APP_BANNER_REDESIGN', false) === 'NO_DISMISS'

  return isBannerVisible ? (
    <Container
      id="app-download-banner"
      className="app-banner"
      fontSize={0}
      m={1}
      borderRadius="lg"
    >
      {!appBannerNoDismiss && (
        <Flex alignItems="center" mr={12}>
          <CloseButton
            style={{ color: '#0068ef' }}
            size={24}
            id="closeBannerBtn"
            data-ga-category="PCLN_HOME"
            data-ga-action="App_Banner"
            data-ga-label="dismiss"
            title="dismiss app banner"
            onClick={closeAppBanner}
          />
        </Flex>
      )}
      <StyledLink
        color="text.lightest"
        href={getLink(
          isAndroid,
          referral,
          clientGUID,
          `${appBannerNoDismiss ? `${pageId}_v4` : `${pageId}_v5`}`
        )}
        data-testid="DASH_MWEB_TAB_GET_APP"
        data-ga-category="PCLN_HOME"
        data-ga-action="App_Banner"
        data-ga-label="click to launch"
      >
        <Flex alignItems="flex-start" pr={12} py={0} width={52}>
          <Image src={appIconUrl} alt="Priceline Icon" />
        </Flex>
        <Flex flexDirection="column" justifyContent="center">
          <Text
            fontWeight="bold"
            textStyle="captionBold"
            color="background.darkest"
          >
            {titleText}
          </Text>
          <Text textStyle="disclaimer" color="background.dark">
            {contentText}
          </Text>
        </Flex>
        <Flex alignItems="center" ml="auto">
          <Box ml="auto" my="auto" pl={1}>
            <Button size="small">Get</Button>
          </Box>
        </Flex>
      </StyledLink>
    </Container>
  ) : null
}
